// Side-by-side duplicate quote comparison. Pick the one to keep, archive the
// rest with the note "Duplicate of <ref>" (restorable from Archived & deleted).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import api from "../../../lib/api";

const GBP = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const itemKey = (i) => String(i.sku || i.name || "").toLowerCase();

function Col({ q, keep, onKeep, onArchive, busy, allKeys, others }) {
  const isKeep = keep === q.id;
  const mine = Object.fromEntries(q.items.map((i) => [itemKey(i), i]));
  const row = (label, val, diff) => (
    <div className="flex justify-between gap-2 text-[11.5px] py-0.5">
      <span className="text-slate-500 font-mono uppercase tracking-wider text-[9.5px]">{label}</span>
      <span className={`text-right truncate ${diff ? "text-amber-200" : "text-slate-200"}`}>{val || "—"}</span>
    </div>
  );
  const differs = (k) => others.some((o) => (o[k] || "") !== (q[k] || ""));
  return (
    <div className={`w-[260px] shrink-0 rounded-lg border p-3 flex flex-col gap-2 ${isKeep ? "border-emerald-500/50 bg-emerald-500/[0.05]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`dupe-col-${q.quote_ref}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-bold text-orange-300 text-[13px]">{q.quote_ref}</span>
        <Link to={`/quotes/${q.id}/edit`} className="text-slate-400 hover:text-white" title="Open quote" data-testid={`dupe-open-${q.quote_ref}`}><ExternalLink className="w-3.5 h-3.5" /></Link>
      </div>
      <div className="border-y border-white/[0.06] py-1.5">
        {row("Status", q.status, differs("status"))}
        {row("Total", GBP(q.total), differs("total"))}
        {row("Created", fmt(q.created))}
        {row("AM", q.am, differs("am"))}
        {row("Created by", q.created_by)}
        {row("Build", q.build_type, differs("build_type"))}
        {row("Delivery", [q.delivery_method, q.delivery_postcode].filter(Boolean).join(" · "))}
      </div>
      <div className="space-y-0.5 min-h-[40px]">
        <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500">{q.items.length} line item{q.items.length === 1 ? "" : "s"}</div>
        {allKeys.map((k) => {
          const it = mine[k];
          const same = it && others.every((o) => o.items.some((x) => itemKey(x) === k && Number(x.qty) === Number(it.qty)));
          return (
            <div key={k} className={`text-[11px] flex justify-between gap-2 ${!it ? "text-slate-600 line-through" : same ? "text-slate-300" : "text-amber-200"}`}>
              <span className="truncate">{it?.name || it?.sku || k || "—"}</span>
              <span className="tabular-nums shrink-0">{it ? `×${it.qty}` : "missing"}</span>
            </div>
          );
        })}
      </div>
      {q.notes && <div className="text-[10.5px] text-slate-400 italic line-clamp-3">{q.notes}</div>}
      <div className="mt-auto flex items-center gap-1.5 pt-1">
        <button type="button" onClick={() => onKeep(q.id)} data-testid={`dupe-keep-${q.quote_ref}`}
          className={`flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md text-[11px] font-semibold border ${isKeep ? "border-emerald-500/60 bg-emerald-500/20 text-emerald-100" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
          <CheckCircle2 className="w-3.5 h-3.5" /> {isKeep ? "Keeping" : "Keep this"}
        </button>
        {!isKeep && (
          <button type="button" onClick={() => onArchive([q])} disabled={busy} data-testid={`dupe-archive-${q.quote_ref}`}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[11px] font-semibold border border-rose-500/40 text-rose-200 hover:bg-rose-500/10 disabled:opacity-50">
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
        )}
      </div>
    </div>
  );
}

export default function DuplicateCompare({ quotes, defaultKeepId, onArchived }) {
  const [keep, setKeep] = useState(defaultKeepId || quotes[0]?.id);
  const [busy, setBusy] = useState(false);
  const allKeys = useMemo(() => [...new Set(quotes.flatMap((q) => q.items.map(itemKey)))], [quotes]);
  const keepRef = quotes.find((q) => q.id === keep)?.quote_ref;
  const rest = quotes.filter((q) => q.id !== keep);

  const archive = async (list) => {
    if (!window.confirm(`Archive ${list.map((q) => q.quote_ref).join(", ")} as duplicate of ${keepRef}? You can restore from Archived & deleted.`)) return;
    setBusy(true);
    try {
      await Promise.all(list.map((q) => api.post(`/quotes/${q.id}/archive`, { reason: `Duplicate of ${keepRef}` })));
      toast.success(`Archived ${list.length} duplicate${list.length === 1 ? "" : "s"} of ${keepRef}`);
      onArchived?.(list.map((q) => q.id));
    } catch { toast.error("Archive failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="dupe-compare">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {quotes.map((q) => (
          <Col key={q.id} q={q} keep={keep} onKeep={setKeep} onArchive={archive} busy={busy} allKeys={allKeys}
            others={quotes.filter((o) => o.id !== q.id)} />
        ))}
      </div>
      {rest.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] text-slate-400">Amber = differs between quotes. Keeping <span className="font-mono text-emerald-300">{keepRef}</span>.</span>
          <button type="button" onClick={() => archive(rest)} disabled={busy} data-testid="dupe-archive-all"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-[12px] font-semibold border border-rose-500/50 bg-rose-500/15 text-rose-100 hover:bg-rose-500/25 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />} Archive {rest.length} other{rest.length === 1 ? "" : "s"} as duplicates
          </button>
        </div>
      )}
    </div>
  );
}
